"use client";

import { useState, useEffect } from "react";
import { ArrowUpDown } from "lucide-react";
import { FaArrowRight } from "react-icons/fa";
import { calculateAmount } from "@sdk/utils";
import { CustomSelect } from "@shared/src/components/ui/common/form-components/CustomSelect";

interface SecondaryAmountInputProps {
  tradeType: "buy" | "sell";
  amount: string;
  setAmount: (value: string) => void;
  fiatCurrency: string;
  currencyOptions: Array<{ value: string; label: string; icon?: React.ReactNode }>;
  handleFiatCurrencyChange: (value: string | string[]) => void;
  paymentMethod: string;
  paymentOptions: Array<{ value: string; label: string; icon?: React.ReactNode }>;
  handlePaymentMethodChange: (value: string | string[]) => void;
  rate: number | null;
  isPrimary?: boolean;
  secondaryAmount: string;
  onChangeSecondary: (value: string) => void;
  onSwap?: () => void;
}

export function SecondaryAmountInput({
  tradeType,
  amount,
  setAmount,
  fiatCurrency,
  currencyOptions,
  handleFiatCurrencyChange,
  paymentMethod,
  paymentOptions,
  handlePaymentMethodChange,
  rate,
  isPrimary = false,
  onChangeSecondary,
  secondaryAmount,
  onSwap,
}: SecondaryAmountInputProps) {
  const effectiveRate = rate || 0;
  const [isInputtingSecondary, setIsInputtingSecondary] = useState(false);

  // Update secondary amount when rate or primary amount changes (but not when user is typing in secondary)
  useEffect(() => {
    if (isInputtingSecondary) return setIsInputtingSecondary(false);
    const newAmount = calculateAmount(amount, effectiveRate, true);
    console.log("useeffect secondary calculateAmount", rate, newAmount);
    onChangeSecondary(newAmount);
  }, [effectiveRate, amount, effectiveRate]);

  const handleSecondaryChange = (value: string) => {
    onChangeSecondary(value);
    setIsInputtingSecondary(true);
    if (!rate) return;
    const newAmount = calculateAmount(value, effectiveRate);
    console.log(" secondary calculateAmount", rate, newAmount);
    setAmount(calculateAmount(value, effectiveRate));
  };

  const labelText = tradeType === "buy" ? "You pay" : "You receive";
  const currencyLabel = currencyOptions.find(c => c.value === fiatCurrency)?.label || fiatCurrency;

  return (
    <div className="border-2 border-border-tertiary bg-surfaces-bg-secondary rounded-5xl min-h-[120px] flex  px-4 md:px-6 font-manrope py-4 group has-[:focus]:border-border-default">
      <div className="flex flex-col w-full">
        <div className="flex-1 w-full">
          <span className="text-small font-extrabold whitespace-nowrap">{labelText}</span>
          <div className="flex items-center gap-1">
            {
              <button
                type="button"
                onClick={onSwap}
                disabled={!isPrimary || !onSwap}
                className=" group/swap text-text-tertiary flex gap-1 items-center  hover:text-text-primary transition-colors"
                aria-label="Swap input order"
              >
                {isPrimary && onSwap && (
                  <span className="text-text-brand hover:text-text-brandsoft  group-hover/swap:text-text-brandsoft transition-colors">
                    <ArrowUpDown size={20} />
                  </span>
                )}{" "}
                {currencyLabel}
              </button>
            }

            <input
              type="number"
              min="0"
              step="any"
              onKeyDown={e => {
                if (e.key === "-" || e.key === "+") {
                  e.preventDefault();
                }
              }}
              className="py-0.5 px-2 pl-0 text-h5 font-unbounded bg-transparent outline-none w-full"
              placeholder="0.00"
              value={secondaryAmount}
              onChange={e => handleSecondaryChange(e.target.value)}
            />
          </div>
        </div>
        
        {/* Currency and Payment Method Selectors */}
        <div className="flex items-center gap-2 justify-end mt-2">
          <div className="flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1 flex-shrink-0 text-text-primary rounded-2xl py-2 px-1 bg-surfaces-bg-secondary-darker text-small justify-center">
              <CustomSelect
                label=""
                hideLabel
                value={fiatCurrency}
                onValueChange={handleFiatCurrencyChange}
                options={currencyOptions}
                variant="dropdown"
                layout="list"
                containerClassName="!min-h-0 !h-auto !border-0 !bg-transparent !p-0 !m-0"
                className="!min-h-0 !h-auto !border-0 !p-0 !m-0 !text-small"
              />
            </div>
            <div className="relative w-fit">
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center justify-center size-6 rounded-xl bg-surfaces-bg-secondary-darker border-2 border-border-secondary z-10 overflow-visible">
                <FaArrowRight size={12} className="text-text-primary" />
              </div>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0 text-text-primary rounded-2xl py-2 px-1 bg-surfaces-bg-secondary-darker text-small justify-center">
              <CustomSelect
                modal={false}
                label=""
                hideLabel
                value={paymentMethod}
                onValueChange={handlePaymentMethodChange}
                options={paymentOptions}
                variant="dropdown"
                layout="list"
                containerClassName="!min-h-0 !h-auto !border-0 !bg-transparent !p-0 !m-0"
                className="!min-h-0 !h-auto !border-0 !p-0 !m-0 !text-small"
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
